import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { RefObject, useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { SidebarStructuredSearchControls } from "./SidebarStructuredSearchControls";
import { appendNestedKeyEqualsStructuredFilter } from "./appendNestedKeyEqualsStructuredFilter";
import { useSidebarStructuredSearchRefresh } from "./hooks/useSidebarStructuredSearchRefresh";

import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import { tracesSearchSchema } from "@/components/StructuredSearch/schemas/traces-search-schema";
import {
  createEmptySidebarStructuredTracesInfiniteData,
  getSidebarStructuredTracesQueryKey,
} from "@/components/SidebarSearchLayout/Sidebar/hooks/useStructuredSidebarTraces";
import {
  ADD_TRACES_STRUCTURED_FILTER_EVENT,
  type AddTracesStructuredFilterEventDetail,
} from "@/components/SidebarSearchLayout/sidebar-tab-context";
import { buildTracesSearchParams } from "@/components/TracesSearch/buildTracesSearchParams";
import { useStructuredTracesRefresh } from "@/components/TracesSearch/hooks/useStructuredTracesRefresh";
import { SESSION_STORAGE } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { isWorkspaceTracesSearchPathname } from "@/utils/workspacePathMatchers";
import { REQUEST_LOGS_SIDEBAR_PAGE_SIZE } from "@/utils/requestLogUrlParams";

export interface TracesDrawerControlsProps {
  inputRef: RefObject<HTMLInputElement | null>;
  onRefresh?: () => void;
  onSearchActiveChange?: (isActive: boolean) => void;
}

export const TracesDrawerControls = observer(
  ({ inputRef, onRefresh, onSearchActiveChange }: TracesDrawerControlsProps) => {
    const navigate = useNavigate();
    const { workspaceId } = useParams();
    const location = useLocation();
    const structuredSearchStore = useStructuredSearchStore();
    const queryClient = useQueryClient();
    const auth = useAuth();
    const userToken = auth?.userToken ?? "";
    const activeWorkspaceId = useUser()?.activeWorkspaceId;
    const { clearNewSpansBadge } = useStructuredTracesRefresh();

    useEffect(() => {
      const handleAddStructuredFilter = (event: Event) => {
        const { detail } = event as CustomEvent<AddTracesStructuredFilterEventDetail>;
        if (!detail) {
          return;
        }

        appendNestedKeyEqualsStructuredFilter(structuredSearchStore, detail);
      };

      window.addEventListener(ADD_TRACES_STRUCTURED_FILTER_EVENT, handleAddStructuredFilter);
      return () =>
        window.removeEventListener(ADD_TRACES_STRUCTURED_FILTER_EVENT, handleAddStructuredFilter);
    }, [structuredSearchStore]);

    const exploreInTablePath = useMemo(() => {
      if (!workspaceId) {
        return null;
      }

      const params = applyStructuredSearchStateToSearchParams(
        new URLSearchParams(),
        {
          filters: structuredSearchStore.filters,
          dateRange: structuredSearchStore.dateRange,
          q: structuredSearchStore.q,
          pageIndex: structuredSearchStore.pageIndex,
          pageSize: structuredSearchStore.pageSize,
          sorting: structuredSearchStore.sorting,
        },
        { resetPage: true, namespace: "tracesTable" },
      );
      const qs = params.toString();
      return `/workspace/${workspaceId}/traces-search${qs ? `?${qs}` : ""}`;
    }, [
      structuredSearchStore.dateRange,
      structuredSearchStore.filters,
      structuredSearchStore.pageIndex,
      structuredSearchStore.pageSize,
      structuredSearchStore.q,
      structuredSearchStore.sorting,
      workspaceId,
    ]);

    const handleExploreInSurface = (path: string | null, isCurrentSurface: boolean) => {
      if (!path) {
        return;
      }

      if (structuredSearchStore.editingFilterId) {
        structuredSearchStore.cancelEdit();
      } else {
        structuredSearchStore.cancelBuilder();
      }
      inputRef.current?.blur();

      if (isCurrentSurface) {
        navigate({ pathname: location.pathname, search: path.split("?")[1] ?? "" });
        return;
      }

      sessionStorage.setItem(
        SESSION_STORAGE.TRACES_RETURN_URL,
        `${location.pathname}${location.search}${location.hash}`,
      );
      navigate(path);
    };

    const handleExploreInTable = () =>
      handleExploreInSurface(
        exploreInTablePath,
        isWorkspaceTracesSearchPathname(location.pathname),
      );

    const resetSidebarQuery = useCallback(() => {
      if (!userToken || typeof activeWorkspaceId !== "number") {
        return;
      }

      const sidebarPageSize =
        structuredSearchStore.pageSize > 0
          ? structuredSearchStore.pageSize
          : REQUEST_LOGS_SIDEBAR_PAGE_SIZE;
      const tracesSearchParams = buildTracesSearchParams(structuredSearchStore);
      const queryKey = getSidebarStructuredTracesQueryKey({
        userToken,
        tracesSearchParams,
        activeWorkspaceId,
        sidebarPageSize,
      });

      queryClient.setQueryData(
        queryKey,
        createEmptySidebarStructuredTracesInfiniteData(sidebarPageSize),
      );

      void queryClient.refetchQueries({ queryKey, exact: true });
    }, [activeWorkspaceId, queryClient, structuredSearchStore, userToken]);

    const handleRefresh = useSidebarStructuredSearchRefresh({
      onRefresh,
      clearNewDataBadge: clearNewSpansBadge,
      resetSidebarQuery,
    });

    return (
      <SidebarStructuredSearchControls
        inputRef={inputRef}
        config={tracesSearchSchema}
        onExploreInTable={handleExploreInTable}
        onRefresh={handleRefresh}
        onSearchActiveChange={onSearchActiveChange}
      />
    );
  },
);
